import { useEffect, useState } from 'react';
import type { PlanStep, Run, StepResult } from '@verifyloop/core';
import type { TestSummary } from '../api/types';
import { getRun, listRuns, patchTest, replacePlan, rerunTest } from '../api/endpoints';
import { describeError } from '../api/client';
import { useResource, useToast } from '../state';
import { formatDuration, formatRelative } from '../lib/format';
import { ArtifactPreview } from './Artifact';
import {
  Mono,
  RunStatusPill,
  RunTriggerTag,
  StepStatusPill,
  StepTypePill,
} from './Pills';
import { PlaybookFlow } from './PlaybookFlow';
import { detectScenarioBranch } from './FeatureGroupedTests';
import { ExportPlaywrightModal } from './ExportPlaywrightModal';
import { StudioStepRecorderModal } from './StudioStepRecorderModal';

export interface UnifiedTestWorkspaceProps {
  test: TestSummary;
  projectId: string;
  onBack: () => void;
  onEditPlan?: () => void;
  onTestChanged?: () => void;
}

export function UnifiedTestWorkspace({
  test,
  projectId: _projectId,
  onBack,
  onEditPlan,
  onTestChanged,
}: UnifiedTestWorkspaceProps): JSX.Element {
  const toast = useToast();
  const branch = detectScenarioBranch(test);

  const [selectedStepIndex, setSelectedStepIndex] = useState<number>(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(
    test.latestRun?.id ?? null,
  );
  const [device, setDevice] = useState<string>(
    typeof test.device === 'string'
      ? test.device
      : (test.device?.name ?? 'desktop-chrome'),
  );

  useEffect(() => {
    setDevice(
      typeof test.device === 'string'
        ? test.device
        : (test.device?.name ?? 'desktop-chrome'),
    );
  }, [test.device]);

  const handleDeviceChange = async (newDevice: string) => {
    setDevice(newDevice);
    try {
      await patchTest(test.id, { device: newDevice as any });
      toast.success('Device preset updated', `Runner viewport set to ${newDevice}`);
      onTestChanged?.();
    } catch (err) {
      toast.error(`Could not update device: ${describeError(err)}`);
    }
  };

  // Fetch all runs specifically for this test
  const runsResource = useResource(
    () => listRuns({ test: test.id, limit: 15 }),
    [test.id],
    { live: ['run.created', 'run.updated', 'run.finished'] },
  );

  const runs: Run[] = runsResource.data?.runs ?? [];

  // Keep selectedRunId synchronized with latest run if not manually picked
  useEffect(() => {
    if (!selectedRunId && runs.length > 0 && runs[0]) {
      setSelectedRunId(runs[0].id);
    }
  }, [runs, selectedRunId]);

  // Fetch full details of the currently selected run
  const activeRunResource = useResource(
    () => (selectedRunId ? getRun(selectedRunId) : Promise.resolve(null)),
    [selectedRunId],
    { live: ['run.updated', 'run.finished'] },
  );

  const activeRun = activeRunResource.data?.run;
  const stepResults: readonly StepResult[] | undefined = activeRun?.steps;

  // Local mutable plan steps for live interactive canvas editing
  const [steps, setSteps] = useState<PlanStep[]>(() => [...test.planSteps]);

  useEffect(() => {
    setSteps([...test.planSteps]);
  }, [test.planSteps]);

  // Selected step data
  const currentStep: PlanStep | undefined = steps[selectedStepIndex] ?? test.planSteps[selectedStepIndex];
  const currentResult: StepResult | undefined = stepResults?.[selectedStepIndex];

  const handleInsertStep = async (afterIndex: number) => {
    const newStep: PlanStep = {
      type: 'action',
      description: 'Interact with target element',
      target: '',
    };
    const next = [...steps.slice(0, afterIndex + 1), newStep, ...steps.slice(afterIndex + 1)];
    setSteps(next);
    setSelectedStepIndex(afterIndex + 1);
    try {
      await replacePlan(test.id, next);
      toast.success('Step inserted', `Added step #${afterIndex + 2}`);
      onTestChanged?.();
    } catch (err) {
      toast.error(`Could not save step: ${describeError(err)}`);
    }
  };

  const handleUpdateStep = async (index: number, updated: PlanStep, healed?: boolean) => {
    const next = steps.map((s, i) => (i === index ? updated : s));
    setSteps(next);
    try {
      await replacePlan(test.id, next, healed);
      toast.success(
        healed ? 'Auto-healed selector applied to plan' : 'Step saved',
        healed ? `Saved resilient locator for step #${index + 1}` : `Updated step #${index + 1}`,
      );
      onTestChanged?.();
    } catch (err) {
      toast.error(`Could not save step: ${describeError(err)}`);
    }
  };

  const handleDeleteStep = async (index: number) => {
    if (steps.length <= 1) {
      toast.error('Cannot delete the only step in a test plan');
      return;
    }
    const next = steps.filter((_, i) => i !== index);
    setSteps(next);
    setSelectedStepIndex(Math.max(0, index - 1));
    try {
      await replacePlan(test.id, next);
      toast.success('Step removed');
      onTestChanged?.();
    } catch (err) {
      toast.error(`Could not delete step: ${describeError(err)}`);
    }
  };

  const handleSimulateStep = async (stepIdx: number) => {
    setSelectedStepIndex(stepIdx);
    toast.info(`Simulating step #${stepIdx + 1}`, 'Executing browser runner…');
    await handleRunTest();
    setSelectedStepIndex(stepIdx);
  };

  const handleAddBranch = async () => {
    const newStep: PlanStep = {
      type: 'assertion',
      description: 'Assert error alert is displayed on invalid submission or failure',
      optional: true,
    };
    const next = [...steps, newStep];
    setSteps(next);
    setSelectedStepIndex(next.length - 1);
    try {
      await replacePlan(test.id, next);
      toast.success('Branch added', 'Negative verification assertion appended to flow');
      onTestChanged?.();
    } catch (err) {
      toast.error(`Could not add branch: ${describeError(err)}`);
    }
  };

  const handleSelectRun = (runId: string) => {
    setSelectedRunId(runId);
    const targetRun = runs.find((r) => r.id === runId);
    if (targetRun) {
      if (targetRun.status === 'failed') {
        const failIdx = targetRun.steps.findIndex((s) => s.status === 'failed');
        if (failIdx >= 0) {
          setSelectedStepIndex(failIdx);
        }
      } else if (targetRun.status === 'passed' && test.planSteps.length > 0) {
        setSelectedStepIndex(test.planSteps.length - 1);
      }
    }
  };

  const handleRunTest = async () => {
    setRunning(true);
    try {
      const accepted = await rerunTest(test.id, { wait: true, timeoutMs: 300_000 });
      if (accepted.runIds.length === 0) {
        toast.push({
          kind: 'error',
          message: `“${test.name}” did not start`,
          detail: accepted.skipped[0]?.reason ?? 'the run queue refused it',
        });
        return;
      }
      const newRunId = accepted.runIds[0] ?? null;
      if (newRunId) {
        setSelectedRunId(newRunId);
        const freshRun = await getRun(newRunId);
        if (freshRun.run.status === 'failed') {
          const failIdx = freshRun.run.steps.findIndex((s) => s.status === 'failed');
          if (failIdx >= 0) {
            setSelectedStepIndex(failIdx);
          }
          toast.push({
            kind: 'error',
            message: `“${test.name}” failed`,
            detail:
              freshRun.run.error ??
              `Step #${(failIdx >= 0 ? failIdx : 0) + 1} failed · ${formatDuration(freshRun.run.durationMs)}`,
          });
        } else if (freshRun.run.status === 'passed') {
          if (test.planSteps.length > 0) {
            setSelectedStepIndex(test.planSteps.length - 1);
          }
          toast.success(
            `“${test.name}” passed`,
            `${formatDuration(freshRun.run.durationMs)} · ${freshRun.run.creditsCharged} credits`,
          );
        } else {
          toast.info(`“${test.name}” finished (${freshRun.run.status})`);
        }
      }
      runsResource.reload();
      activeRunResource.reload();
      onTestChanged?.();
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="unified-test-workspace" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* 1. Workspace Header */}
      <div
        className="workspace-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          padding: '12px 16px',
          background: 'var(--bg-inset)',
          border: '1px solid var(--border)',
          borderRadius: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            type="button"
            className="btn btn--small btn--ghost"
            onClick={onBack}
            title="Return to tests list"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            ← Back to Tests
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{test.name}</h2>
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                padding: '2px 7px',
                borderRadius: 4,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                background:
                  branch === 'positive'
                    ? 'rgba(16, 185, 129, 0.15)'
                    : branch === 'negative'
                      ? 'rgba(239, 68, 68, 0.15)'
                      : 'rgba(245, 158, 11, 0.15)',
                color:
                  branch === 'positive'
                    ? '#34d399'
                    : branch === 'negative'
                      ? '#f87171'
                      : '#fbbf24',
                border: `1px solid ${
                  branch === 'positive'
                    ? 'rgba(16, 185, 129, 0.3)'
                    : branch === 'negative'
                      ? 'rgba(239, 68, 68, 0.3)'
                      : 'rgba(245, 158, 11, 0.3)'
                }`,
              }}
            >
              {branch === 'positive'
                ? '🟢 Positive Path'
                : branch === 'negative'
                  ? '🔴 Negative Scenario'
                  : '⚡ Edge Case'}
            </span>
            <span className={`pill pill--${test.type === 'frontend' ? 'info' : 'neutral'} pill--subtle`}>
              {test.type}
            </span>
            {test.healedAt ? <span className="pill pill--info">auto-healed</span> : null}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {test.type === 'frontend' ? (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                background: 'var(--bg-inset)',
                padding: '3px 8px',
                borderRadius: 6,
                border: '1px solid var(--border)',
              }}
            >
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>Device:</span>
              <select
                value={device}
                onChange={(e) => void handleDeviceChange(e.target.value)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text)',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                <option value="desktop-chrome">💻 Desktop (1280x800)</option>
                <option value="iphone-15">📱 iPhone 15 (393x852 Touch)</option>
                <option value="pixel-7">📱 Pixel 7 (412x915 Touch)</option>
                <option value="ipad-pro">📟 iPad Pro (1024x1366)</option>
              </select>
            </div>
          ) : null}

          <button
            type="button"
            className="btn btn--small btn--ghost"
            onClick={() => setExportOpen(true)}
            title="Export this scenario as a standalone Playwright TypeScript (.spec.ts) file"
          >
            📥 Export .spec.ts
          </button>
          <button
            type="button"
            className="btn btn--small btn--ghost"
            onClick={() => setRecorderOpen(true)}
            title="Open interactive step recorder modal"
          >
            ⏺️ Record Step
          </button>
          <button
            type="button"
            className="btn btn--small btn--primary"
            disabled={running}
            onClick={() => void handleRunTest()}
            style={{ fontWeight: 600 }}
          >
            {running ? 'Running Test…' : '▶ Run Test'}
          </button>
          {onEditPlan ? (
            <button type="button" className="btn btn--small btn--ghost" onClick={onEditPlan}>
              Edit Plan
            </button>
          ) : null}
        </div>
      </div>

      {branch === 'negative' ? (
        <div
          style={{
            padding: '8px 12px',
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.25)',
            borderRadius: 6,
            color: '#f87171',
            fontSize: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span>🔴</span>
          <span>
            <strong>Negative Verification Branch:</strong> This scenario asserts that the application correctly handles invalid inputs, bad requests, or error states and prevents unauthorized behavior.
          </span>
        </div>
      ) : null}

      {test.description ? (
        <p className="muted" style={{ margin: '0 4px', fontSize: 13 }}>
          {test.description}
        </p>
      ) : null}

      {/* 2. In-Context Runs Timeline Selector Strip (No Tab Switching!) */}
      <div
        className="workspace-runs-bar"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          background: 'var(--bg-panel)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          overflowX: 'auto',
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--text-muted)',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span>RUNS ({runs.length}):</span>
        </span>

        {runsResource.loading && runs.length === 0 ? (
          <span className="text-muted" style={{ fontSize: 12 }}>
            Loading runs…
          </span>
        ) : null}

        {runs.length === 0 && !runsResource.loading ? (
          <span className="text-muted" style={{ fontSize: 12, fontStyle: 'italic' }}>
            No runs recorded yet. Click "▶ Run Test" to execute.
          </span>
        ) : null}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 'max-content' }}>
          {runs.map((r, idx) => {
            const isSelected = r.id === selectedRunId;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => handleSelectRun(r.id)}
                aria-pressed={isSelected}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 12px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  background: isSelected ? 'var(--primary, #2563eb)' : 'var(--bg-inset)',
                  color: isSelected ? '#ffffff' : 'var(--text)',
                  border: `1px solid ${isSelected ? '#3b82f6' : 'var(--border)'}`,
                  fontSize: 12,
                  fontWeight: 500,
                  transition: 'all 0.15s ease',
                  boxShadow: isSelected ? '0 2px 8px rgba(37, 99, 235, 0.35)' : undefined,
                }}
              >
                <span style={{ fontWeight: 700 }}>
                  {idx === 0 ? 'Latest' : `#${runs.length - idx}`}
                </span>
                <RunStatusPill status={r.status} />
                {r.durationMs !== undefined ? (
                  <span style={{ opacity: 0.85, fontSize: 11 }}>{formatDuration(r.durationMs)}</span>
                ) : null}
                <span style={{ opacity: 0.65, fontSize: 11 }}>{formatRelative(r.startedAt)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 3. Playbook Flow Studio Canvas (Full Width 100%) */}
      <div
        className="panel playbook-studio-panel"
        style={{
          background: 'var(--bg-panel)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h3 style={{ margin: 0, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
              ⚡ Playbook Flow Studio ({steps.length} steps)
            </h3>
            {currentStep ? (
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                Active: Step #{selectedStepIndex + 1}
              </span>
            ) : null}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {currentStep ? (
              <button
                type="button"
                className="btn btn--small btn--secondary"
                onClick={() => setDrawerOpen(true)}
                title="Inspect step details, selector, screenshot and logs"
                style={{ fontSize: 11, padding: '3px 10px', fontWeight: 600 }}
              >
                🔍 Inspect Step #{selectedStepIndex + 1}
              </button>
            ) : null}
          </div>
        </div>

        <PlaybookFlow
          steps={steps}
          stepResults={stepResults}
          selectedStepIndex={selectedStepIndex}
          onSelectStep={(idx) => {
            setSelectedStepIndex(idx);
            setDrawerOpen(true);
          }}
          onInsertStep={handleInsertStep}
          onSimulateStep={handleSimulateStep}
          onAddBranch={handleAddBranch}
          onExportPlaywright={() => setExportOpen(true)}
          activeRunningIndex={running ? 0 : null}
          showSwitcher={false}
        />
      </div>

      {/* 4. Slide-Over Step Record & Diagnostics Drawer */}
      {drawerOpen && currentStep ? (
        <div
          className="drawer-backdrop"
          onClick={() => setDrawerOpen(false)}
          style={{ zIndex: 70 }}
        >
          <div
            className="drawer"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(560px, 94vw)',
              background: 'var(--bg-panel)',
              borderLeft: '1px solid var(--border)',
              boxShadow: '-10px 0 35px rgba(0, 0, 0, 0.3)',
            }}
          >
            <header
              className="drawer__header"
              style={{
                padding: '14px 18px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--bg-panel)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)' }}>
                  STEP #{selectedStepIndex + 1} INSPECTOR
                </span>
                <StepTypePill type={currentStep.type} />
                {currentResult ? <StepStatusPill status={currentResult.status} /> : null}
              </div>

              <button
                type="button"
                className="btn btn--small btn--ghost"
                onClick={() => setDrawerOpen(false)}
                title="Close drawer"
                style={{ fontSize: 12, padding: '3px 8px', fontWeight: 700 }}
              >
                ✕ Close
              </button>
            </header>

            <div style={{ padding: 18, overflowY: 'auto', flex: 1 }}>
              <StepRecordInspector
                stepIndex={selectedStepIndex}
                step={currentStep}
                result={currentResult}
                activeRun={activeRun}
                onUpdateStep={handleUpdateStep}
                onDeleteStep={(idx) => {
                  handleDeleteStep(idx);
                  setDrawerOpen(false);
                }}
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* 5. Modal Dialogs */}
      {exportOpen ? (
        <ExportPlaywrightModal test={test} onClose={() => setExportOpen(false)} />
      ) : null}

      {recorderOpen ? (
        <StudioStepRecorderModal
          currentStepCount={steps.length}
          onClose={() => setRecorderOpen(false)}
          onAppendStep={async (newStep) => {
            const next = [...steps, newStep];
            setSteps(next);
            setSelectedStepIndex(next.length - 1);
            try {
              await replacePlan(test.id, next);
              toast.success('Step recorded', `Appended step #${next.length}`);
              onTestChanged?.();
            } catch (err) {
              toast.error(`Could not save step: ${describeError(err)}`);
            }
          }}
        />
      ) : null}
    </div>
  );
}

// ================================================================================
// IN-CONTEXT STEP RECORD & ARTIFACT INSPECTOR COMPONENT
// ================================================================================
function StepRecordInspector({
  stepIndex,
  step,
  result,
  activeRun,
  onUpdateStep,
  onDeleteStep,
}: {
  stepIndex: number;
  step?: PlanStep;
  result?: StepResult;
  activeRun?: Run;
  onUpdateStep?: (index: number, updated: PlanStep, healed?: boolean) => void;
  onDeleteStep?: (index: number) => void;
}): JSX.Element {
  const [tab, setTab] = useState<'edit' | 'evidence' | 'logs'>('edit');
  const [draftType, setDraftType] = useState<string>(step?.type ?? 'action');
  const [draftDescription, setDraftDescription] = useState<string>(step?.description ?? '');
  const [draftTarget, setDraftTarget] = useState<string>(step?.target ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (step) {
      setDraftType(step.type);
      setDraftDescription(step.description);
      setDraftTarget(step.target ?? '');
    }
  }, [step]);

  if (!step) {
    return (
      <div className="panel" style={{ padding: 20, textAlign: 'center' }}>
        <p className="muted">No step selected. Click any node on the Playbook canvas to inspect or edit.</p>
      </div>
    );
  }

  const handleSave = () => {
    if (!draftDescription.trim()) return;
    setSaving(true);
    try {
      onUpdateStep?.(stepIndex, {
        ...step,
        type: draftType as any,
        description: draftDescription.trim(),
        target: draftTarget.trim() || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="panel step-record-panel"
      style={{
        background: 'var(--bg-panel)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        minWidth: 320,
      }}
    >
      {/* Header Bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
          paddingBottom: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '0.04em',
              background: 'rgba(59, 130, 246, 0.1)',
              color: '#3b82f6',
              padding: '2px 8px',
              borderRadius: 4,
            }}
          >
            STEP #{stepIndex + 1}
          </span>
          <StepTypePill type={step.type} />
          {result ? <StepStatusPill status={result.status} /> : null}
        </div>

        {result?.durationMs !== undefined ? (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Duration: {formatDuration(result.durationMs)}
          </span>
        ) : null}
      </div>

      {/* 3-Tab Segment Selector */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 4,
          background: 'var(--bg-inset)',
          padding: 3,
          borderRadius: 6,
          border: '1px solid var(--border)',
        }}
      >
        <button
          type="button"
          onClick={() => setTab('edit')}
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: '5px 4px',
            borderRadius: 4,
            border: 'none',
            background: tab === 'edit' ? 'var(--bg-panel)' : 'transparent',
            color: tab === 'edit' ? 'var(--text)' : 'var(--text-muted)',
            cursor: 'pointer',
            textAlign: 'center',
            boxShadow: tab === 'edit' ? '0 1px 3px rgba(0,0,0,0.1)' : undefined,
          }}
        >
          ⚙️ Edit Step
        </button>

        <button
          type="button"
          onClick={() => setTab('evidence')}
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: '5px 4px',
            borderRadius: 4,
            border: 'none',
            background: tab === 'evidence' ? 'var(--bg-panel)' : 'transparent',
            color: tab === 'evidence' ? 'var(--text)' : 'var(--text-muted)',
            cursor: 'pointer',
            textAlign: 'center',
            boxShadow: tab === 'evidence' ? '0 1px 3px rgba(0,0,0,0.1)' : undefined,
          }}
        >
          📸 Media ({result?.artifacts?.length ?? 0})
        </button>

        <button
          type="button"
          onClick={() => setTab('logs')}
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: '5px 4px',
            borderRadius: 4,
            border: 'none',
            background: tab === 'logs' ? 'var(--bg-panel)' : 'transparent',
            color: tab === 'logs' ? 'var(--text)' : 'var(--text-muted)',
            cursor: 'pointer',
            textAlign: 'center',
            boxShadow: tab === 'logs' ? '0 1px 3px rgba(0,0,0,0.1)' : undefined,
          }}
        >
          📋 Logs & Run
        </button>
      </div>

      {/* TAB 1: EDIT STEP INTENT & TARGET */}
      {tab === 'edit' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Step Type
            </label>
            <select
              value={draftType}
              onChange={(e) => setDraftType(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 8px',
                background: 'var(--bg-inset)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: 'var(--text)',
                fontSize: 12.5,
              }}
            >
              <option value="navigation">🌐 navigation (Go to URL/route)</option>
              <option value="action">👆 action (Click, fill, submit element)</option>
              <option value="assertion">🎯 assertion (Verify text, element, alert)</option>
              <option value="wait">⏳ wait (Delay or wait for selector)</option>
              <option value="extract">📥 extract (Extract value from page)</option>
            </select>
          </div>

          <div>
            <label style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Instruction / Intent
            </label>
            <textarea
              rows={3}
              value={draftDescription}
              onChange={(e) => setDraftDescription(e.target.value)}
              placeholder="e.g. Fill email with {{user.email}} and click submit"
              style={{
                width: '100%',
                padding: '8px 10px',
                background: 'var(--bg-inset)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: 'var(--text)',
                fontSize: 13,
                resize: 'vertical',
                lineHeight: 1.4,
              }}
            />
            <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2 }}>
              Supports dynamic tokens: <code>&#123;&#123;user.email&#125;&#125;</code>, <code>&#123;&#123;user.password&#125;&#125;</code>, <code>&#123;&#123;timestamp&#125;&#125;</code>
            </div>
          </div>

          {/* AI AUTO-HEAL DIFF CARD */}
          {result?.healProposal || step.healed ? (
            <div
              style={{
                background: 'rgba(6, 182, 212, 0.08)',
                border: '1px solid rgba(6, 182, 212, 0.35)',
                borderRadius: 8,
                padding: '10px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: '#06b6d4',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  <span>🪄</span>
                  <span>AI AUTO-HEALED SELECTOR</span>
                </span>
                {(result?.healProposal?.confidence ?? step.healed?.confidence) !== undefined ? (
                  <span style={{ fontSize: 10.5, color: '#0891b2', fontWeight: 600 }}>
                    Confidence: {Math.round(((result?.healProposal?.confidence ?? step.healed?.confidence) ?? 1) * 100)}%
                  </span>
                ) : null}
              </div>

              {(result?.healProposal?.previousTarget ?? step.healed?.previousTarget) ? (
                <div>
                  <span style={{ fontSize: 10, color: 'var(--text-muted)', display: 'block' }}>
                    Stale locator (before):
                  </span>
                  <div
                    style={{
                      fontSize: 11,
                      fontFamily: 'monospace',
                      color: 'var(--fail)',
                      background: 'rgba(239, 68, 68, 0.1)',
                      padding: '2px 6px',
                      borderRadius: 4,
                      textDecoration: 'line-through',
                      marginTop: 2,
                    }}
                  >
                    {result?.healProposal?.previousTarget ?? step.healed?.previousTarget}
                  </div>
                </div>
              ) : null}

              <div>
                <span style={{ fontSize: 10, color: 'var(--pass)', display: 'block', fontWeight: 600 }}>
                  Repaired resilient selector (after):
                </span>
                <div
                  style={{
                    fontSize: 11,
                    fontFamily: 'monospace',
                    color: 'var(--pass)',
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.25)',
                    padding: '3px 6px',
                    borderRadius: 4,
                    marginTop: 2,
                  }}
                >
                  {result?.healProposal?.repairedTarget ?? step.healed?.repairedTarget ?? step.target}
                </div>
              </div>

              {result?.healProposal?.matchedLabel ? (
                <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2 }}>
                  Matched element: <em>&ldquo;{result.healProposal.matchedLabel}&rdquo;</em>
                </div>
              ) : null}

              {/* 1-Click Commit Healed Selector to Plan */}
              {result?.healProposal && (!step.healed || step.healed.repairedTarget !== result.healProposal.repairedTarget) ? (
                <button
                  type="button"
                  className="btn btn--small"
                  onClick={() => {
                    const repaired = result.healProposal!.repairedTarget;
                    setDraftTarget(repaired);
                    const updatedStep: PlanStep = {
                      ...step,
                      target: repaired,
                      healed: {
                        previousTarget: result.healProposal!.previousTarget ?? step.target,
                        repairedTarget: repaired,
                        confidence: result.healProposal!.confidence,
                        healedAt: new Date().toISOString(),
                      },
                    };
                    onUpdateStep?.(stepIndex, updatedStep, true);
                  }}
                  style={{
                    marginTop: 8,
                    background: '#0891b2',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 6,
                    padding: '6px 12px',
                    fontWeight: 700,
                    fontSize: 11.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    cursor: 'pointer',
                    boxShadow: '0 1px 4px rgba(8, 145, 178, 0.25)',
                  }}
                  title="Make this repaired locator permanent by updating and saving the test plan"
                >
                  <span>🪄</span>
                  <span>Apply Healed Selector to Plan</span>
                </button>
              ) : null}
            </div>
          ) : null}

          <div>
            <label style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
              Target Selector or URL
            </label>
            <input
              type="text"
              value={draftTarget}
              onChange={(e) => setDraftTarget(e.target.value)}
              placeholder="e.g. /login or input[name='email'] or #submit-btn"
              style={{
                width: '100%',
                padding: '6px 10px',
                background: 'var(--bg-inset)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: 'var(--text)',
                fontSize: 12,
                fontFamily: 'monospace',
              }}
            />
            {result?.resolvedTarget && result.resolvedTarget !== draftTarget ? (
              <button
                type="button"
                className="btn btn--small btn--ghost"
                onClick={() => setDraftTarget(result.resolvedTarget!)}
                style={{ fontSize: 11, padding: '2px 6px', color: 'var(--accent-text)', marginTop: 4 }}
                title="Populate target with Playwright's actual resolved element"
              >
                🎯 Use Resolved: {result.resolvedTarget}
              </button>
            ) : null}
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 6, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
            {onDeleteStep ? (
              <button
                type="button"
                className="btn btn--small btn--ghost btn--danger"
                onClick={() => onDeleteStep(stepIndex)}
                style={{ fontSize: 11 }}
              >
                Delete Step
              </button>
            ) : <span />}

            <button
              type="button"
              className="btn btn--small btn--primary"
              onClick={handleSave}
              disabled={saving || !draftDescription.trim()}
              style={{ fontWeight: 600, fontSize: 12 }}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      ) : null}

      {/* TAB 2: FORENSIC EVIDENCE & SCREENSHOTS */}
      {tab === 'evidence' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {activeRun && result?.artifacts && result.artifacts.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {result.artifacts.map((artifact) => (
                <div
                  key={artifact.id}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: 10,
                    background: 'var(--bg-inset)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 8,
                      fontSize: 11,
                      color: 'var(--text-muted)',
                    }}
                  >
                    <span style={{ fontWeight: 700, textTransform: 'uppercase', color: 'var(--accent-text)' }}>
                      {artifact.kind}
                    </span>
                    <Mono>{artifact.id}</Mono>
                  </div>
                  <ArtifactPreview
                    runId={activeRun.id}
                    artifact={artifact}
                    onPickSelector={(picked) => {
                      setDraftTarget(picked);
                      setTab('edit');
                    }}
                  />
                </div>
              ))}
            </div>
          ) : activeRun && result?.status === 'passed' ? (
            <div
              style={{
                padding: 12,
                borderRadius: 6,
                background: 'rgba(16, 185, 129, 0.08)',
                border: '1px solid rgba(16, 185, 129, 0.25)',
                color: '#34d399',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span>✓</span>
              <span>
                Step passed cleanly in {formatDuration(result.durationMs)}. Evidence captured on failure or in the full run video.
              </span>
            </div>
          ) : !activeRun ? (
            <div
              style={{
                padding: 14,
                borderRadius: 6,
                background: 'var(--bg-inset)',
                border: '1px dashed var(--border)',
                color: 'var(--text-muted)',
                fontSize: 12,
                textAlign: 'center',
              }}
            >
              No run recorded yet. Click <strong>"▶ Run Test"</strong> above to capture execution evidence.
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', padding: 8 }}>
              No media artifacts captured for this step.
            </div>
          )}
        </div>
      ) : null}

      {/* TAB 3: LOGS & DIAGNOSTICS */}
      {tab === 'logs' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {result?.healProposal || step.healed ? (
            <div
              style={{
                padding: '8px 10px',
                borderRadius: 6,
                background: 'rgba(6, 182, 212, 0.1)',
                border: '1px solid rgba(6, 182, 212, 0.3)',
                color: '#06b6d4',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 6,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>🪄</span>
                <span>
                  <strong>Auto-Healed:</strong> Locator repaired to <code>{result?.healProposal?.repairedTarget ?? step.healed?.repairedTarget ?? step.target}</code>
                </span>
              </div>
              {result?.healProposal && (!step.healed || step.healed.repairedTarget !== result.healProposal.repairedTarget) ? (
                <button
                  type="button"
                  className="btn btn--small"
                  onClick={() => {
                    const repaired = result.healProposal!.repairedTarget;
                    setDraftTarget(repaired);
                    const updatedStep: PlanStep = {
                      ...step,
                      target: repaired,
                      healed: {
                        previousTarget: result.healProposal!.previousTarget ?? step.target,
                        repairedTarget: repaired,
                        confidence: result.healProposal!.confidence,
                        healedAt: new Date().toISOString(),
                      },
                    };
                    onUpdateStep?.(stepIndex, updatedStep, true);
                  }}
                  style={{
                    background: '#0891b2',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    padding: '3px 8px',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Apply to Plan
                </button>
              ) : null}
            </div>
          ) : null}

          {result?.resolvedTarget ? (
            <div>
              <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 600 }}>
                Resolved Target (Playwright)
              </div>
              <div style={{ fontSize: 12, marginTop: 2, color: 'var(--accent-text)' }}>
                <Mono>{result.resolvedTarget}</Mono>
              </div>
            </div>
          ) : null}

          {result?.error ? (
            <div
              style={{
                padding: 10,
                borderRadius: 6,
                background: 'rgba(239, 68, 68, 0.12)',
                border: '1px solid #ef4444',
                color: '#f87171',
                fontSize: 12,
              }}
            >
              <strong style={{ display: 'block', marginBottom: 4 }}>Execution Error:</strong>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 11.5 }}>
                {result.error}
              </pre>
            </div>
          ) : null}

          {result?.extractedValue !== undefined ? (
            <div style={{ background: 'var(--bg-inset)', padding: 10, borderRadius: 6 }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 600 }}>
                Extracted Value
              </div>
              <div style={{ fontSize: 12, marginTop: 2 }}>
                <Mono>
                  {typeof result.extractedValue === 'string'
                    ? result.extractedValue
                    : JSON.stringify(result.extractedValue, null, 2)}
                </Mono>
              </div>
            </div>
          ) : null}

          {activeRun ? (
            <div
              style={{
                borderTop: '1px solid var(--border)',
                paddingTop: 8,
                fontSize: 11,
                color: 'var(--text-muted)',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <div>
                Run ID: <Mono>{activeRun.id.slice(0, 16)}…</Mono>
              </div>
              <div>
                Trigger: <RunTriggerTag trigger={activeRun.trigger} />
              </div>
              <div>
                Started: {formatRelative(activeRun.startedAt)}
              </div>
            </div>
          ) : (
            <div className="muted" style={{ fontSize: 12, fontStyle: 'italic' }}>
              No active execution run.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
